import { describe, it, expect, beforeAll, jest, afterEach } from '@jest/globals';
import { readConfig } from './readConfig.js';
import { createTestFileStructure, removeTempDir, type TestFileStructure } from '@ms-cloudpack/test-utilities';
import type { AppConfig, Features, GeneratedConfig, PackageJson, PackageSettings } from '@ms-cloudpack/common-types';
import type { DefineFlags } from '@ms-cloudpack/common-types-browser';
import path from 'path';

describe('readConfig', () => {
  let consoleWarnMock: jest.SpiedFunction<typeof console.warn>;
  let appPath: string;

  async function createFixture(params: {
    appConfig?: AppConfig;
    generatedConfig?: GeneratedConfig;
    packageJson?: Partial<PackageJson>;
    files?: TestFileStructure;
  }) {
    const { appConfig, generatedConfig, packageJson, files } = params;
    return createTestFileStructure({
      ...(appConfig && { 'cloudpack.config.json': appConfig }),
      ...(generatedConfig && { 'cloudpack.generated.json': generatedConfig }),
      ...(packageJson && { 'package.json': { name: 'test', version: '1.0.0', ...packageJson } }),
      ...files,
    });
  }

  beforeAll(() => {
    consoleWarnMock = jest.spyOn(console, 'warn').mockImplementation(() => {});
  });

  afterEach(async () => {
    consoleWarnMock.mockClear();
    appPath && (await removeTempDir(appPath));
    appPath = '';
  });

  it('reads a config file', async () => {
    appPath = await createFixture({
      appConfig: { helpMessage: 'help' },
    });

    const config = await readConfig(appPath);

    expect(config).toEqual({
      appPath,
      mode: 'library',
      helpMessage: 'help',
      generated: {},
      generatedSnapshot: {},
      configPaths: {
        appConfigPaths: [path.join(appPath, 'cloudpack.config.json')],
        generatedConfigPath: path.join(appPath, 'cloudpack.generated.json'),
      },
    });
  });

  it('uses provided mode', async () => {
    appPath = await createFixture({
      appConfig: { helpMessage: 'help' },
    });

    const config = await readConfig(appPath, { mode: 'production' });

    expect(config).toEqual({
      appPath,
      mode: 'production',
      helpMessage: 'help',
      generated: {},
      generatedSnapshot: {},
      configPaths: {
        appConfigPaths: [path.join(appPath, 'cloudpack.config.json')],
        generatedConfigPath: path.join(appPath, 'cloudpack.generated.json'),
      },
    });
  });

  it('returns user and generated package settings separately', async () => {
    appPath = await createFixture({
      appConfig: { packageSettings: [{ match: { name: 'foo' }, exports: { '.': './index.js' } }] },
      generatedConfig: {
        packageSettings: [{ match: { name: 'foo', version: '1.0.0' }, exports: { './bar': './bar.js' } }],
      },
    });

    const config = await readConfig(appPath);

    expect(config).toEqual({
      appPath,
      mode: 'library',
      packageSettings: [{ match: { name: 'foo' }, exports: { '.': './index.js' } }],
      generated: {
        packageSettings: [{ match: { name: 'foo', version: '1.0.0' }, exports: { './bar': './bar.js' } }],
      },
      generatedSnapshot: {
        packageSettings: [{ match: { name: 'foo', version: '1.0.0' }, exports: { './bar': './bar.js' } }],
      },
      configPaths: {
        appConfigPaths: [path.join(appPath, 'cloudpack.config.json')],
        generatedConfigPath: path.join(appPath, 'cloudpack.generated.json'),
      },
    });
  });

  it('includes extraPackageSettings at end of packageSettings', async () => {
    const packageSettings: PackageSettings[] = [{ match: 'foo', exports: { '.': './index.js' } }];
    // This simulates passing --bundler on the command line.
    const extraPackageSettings: PackageSettings[] = [{ match: '*', bundler: 'rollup' }];

    appPath = await createFixture({
      appConfig: { packageSettings },
      packageJson: {},
    });

    const config = await readConfig(appPath, { extraPackageSettings });

    expect(config).toEqual({
      appPath,
      packageSettings: [...packageSettings, ...extraPackageSettings],
      mode: 'library',
      generated: {},
      generatedSnapshot: {},
      configPaths: {
        appConfigPaths: [path.join(appPath, 'cloudpack.config.json')],
        generatedConfigPath: path.join(appPath, 'cloudpack.generated.json'),
      },
    });
  });

  it('merges extraFeatures', async () => {
    const features: Features = { disableSourceMaps: true, enqueueDependencies: false };
    const extraFeatures: Features = { enqueueDependencies: true, evaluateDynamicImports: false };

    appPath = await createFixture({
      appConfig: { features },
    });

    const config = await readConfig(appPath, { extraFeatures });
    expect(config.features).toEqual({ ...features, ...extraFeatures });
  });

  it('merges define flags', async () => {
    const define: DefineFlags = { foo: true, bar: true, 'foo.bar': true, 'foo.baz': true };
    const extraDefineFlags: DefineFlags = { baz: false, 'foo.baz': false };

    appPath = await createFixture({
      appConfig: { define },
    });

    const config = await readConfig(appPath, { extraDefineFlags });
    expect(config.define).toEqual({ ...define, ...extraDefineFlags });
  });

  it("returns an empty/default config if user or generated config doesn't exist", async () => {
    appPath = await createFixture({
      packageJson: {},
    });

    const config = await readConfig(appPath);

    expect(config).toEqual({
      appPath,
      mode: 'library',
      generated: {},
      generatedSnapshot: {},
      configPaths: {
        appConfigPaths: [],
        generatedConfigPath: path.join(appPath, 'cloudpack.generated.json'),
      },
    });
  });

  describe('routes', () => {
    it('converts a single route entry string into an expanded source path', async () => {
      appPath = await createFixture({
        packageJson: { name: 'test' },
        appConfig: {
          routes: [{ match: '/', entry: './src/foo.ts' }],
        },
        files: { './src/foo.ts': '' },
      });

      const config = await readConfig(appPath);

      expect(config.routes).toEqual([
        {
          match: '/',
          entry: [{ importPath: './lib/foo.js', sourcePath: './src/foo.ts' }],
        },
      ]);
    });

    it('converts an array of route entries into expanded source paths', async () => {
      appPath = await createFixture({
        packageJson: { name: 'test' },
        appConfig: {
          routes: [{ match: '/', entry: ['./src/foo.ts', './src/bar.ts'] }],
        },
        files: {
          './src/foo.ts': '',
          './src/bar.ts': '',
        },
      });

      const config = await readConfig(appPath);

      expect(config.routes).toEqual([
        {
          match: '/',
          entry: [
            { importPath: './lib/foo.js', sourcePath: './src/foo.ts' },
            { importPath: './lib/bar.js', sourcePath: './src/bar.ts' },
          ],
        },
      ]);
    });

    it('converts exportEntry to entry and preserves the import path', async () => {
      appPath = await createFixture({
        packageJson: {
          exports: {
            '.': {
              import: './lib/index.js',
            },
          },
        },
        appConfig: {
          routes: [{ match: '/', exportEntry: '.' }],
        },
        files: {
          'src/index.ts': '',
          'lib/index.js': '',
        },
      });

      const config = await readConfig(appPath);

      expect(config.routes).toEqual([
        {
          match: '/',
          entry: [{ importPath: '.', sourcePath: './src/index.ts' }],
        },
      ]);
    });

    it("handles an entry that's an exports map key", async () => {
      appPath = await createFixture({
        packageJson: {
          // non-standard remapping from import path to source path
          exports: { './foo': './bar.js' },
        },
        appConfig: {
          routes: [{ match: '/', entry: './foo' }],
        },
        files: { 'bar.js': '' },
      });

      const config = await readConfig(appPath);

      expect(config.routes).toEqual([
        {
          match: '/',
          entry: [{ importPath: './foo', sourcePath: './bar.js' }],
        },
      ]);
    });

    it('converts exportEntry to entry and fills in a missing import path', async () => {
      appPath = await createFixture({
        packageJson: {
          module: './lib/index.js',
        },
        appConfig: {
          routes: [{ match: '/', exportEntry: '.' }],
        },
        files: {
          'src/index.ts': '',
          'lib/index.js': '',
        },
      });

      const config = await readConfig(appPath);

      expect(config.routes).toEqual([
        {
          match: '/',
          entry: [{ importPath: '.', sourcePath: './src/index.ts' }],
        },
      ]);
    });

    it('expands serverEntry (file path or exports key)', async () => {
      appPath = await createFixture({
        packageJson: { exports: { './bar': './lib/bar.js' } },
        appConfig: {
          routes: [
            { match: '/', serverEntry: './src/foo.ts' }, // file path
            { match: '/bar', serverEntry: './bar' }, // exports key
          ],
        },
        files: { './src/bar.ts': '', './src/foo.ts': '' },
      });

      const config = await readConfig(appPath);

      expect(config.routes).toEqual([
        {
          match: '/',
          serverEntry: { importPath: './lib/foo.js', sourcePath: './src/foo.ts' },
        },
        {
          match: '/bar',
          serverEntry: { importPath: './bar', sourcePath: './src/bar.ts' },
        },
      ]);
    });
  });

  describe('with custom config filename', () => {
    it('reads config from custom filename', async () => {
      appPath = await createTestFileStructure({
        'my-config.json': { helpMessage: 'custom config' } satisfies AppConfig,
        'cloudpack.config.json': { helpMessage: 'default config' } satisfies AppConfig,
      });

      const config = await readConfig(appPath, { configFileName: 'my-config.json' });

      expect(config.helpMessage).toBe('custom config');
      expect(config.appPath).toBe(appPath);
      expect(config.mode).toBe('library');
    });

    it('reads generated config with custom app config filename', async () => {
      appPath = await createTestFileStructure({
        'custom.json': { packageSettings: [{ match: 'foo' }] } satisfies AppConfig,
        'cloudpack.generated.json': {
          packageSettings: [{ match: { name: 'bar', version: '1.0.0' } }],
        } satisfies GeneratedConfig,
      });

      const config = await readConfig(appPath, { configFileName: 'custom.json' });

      expect(config.packageSettings).toEqual([{ match: 'foo' }]);
      expect(config.generated?.packageSettings).toEqual([{ match: { name: 'bar', version: '1.0.0' } }]);
    });

    it('applies mode option with custom config filename', async () => {
      appPath = await createTestFileStructure({
        'production.config.json': { helpMessage: 'prod config' } satisfies AppConfig,
      });

      const config = await readConfig(appPath, {
        configFileName: 'production.config.json',
        mode: 'production',
      });

      expect(config.mode).toBe('production');
      expect(config.helpMessage).toBe('prod config');
    });

    it('merges extra settings with custom config', async () => {
      const extraPackageSettings: PackageSettings[] = [{ match: 'extra-package', bundler: 'webpack' }];
      const extraFeatures: Features = { disableSourceMaps: true };
      const extraDefineFlags: DefineFlags = { DEBUG: 'true' };

      appPath = await createTestFileStructure({
        'app.config.json': {
          packageSettings: [{ match: 'base-package' }],
          features: { enableCloudHosted: false },
          define: { APP_NAME: '"MyApp"' },
        } satisfies AppConfig,
      });

      const config = await readConfig(appPath, {
        configFileName: 'app.config.json',
        extraPackageSettings,
        extraFeatures,
        extraDefineFlags,
      });

      expect(config.packageSettings).toEqual([
        { match: 'base-package' },
        { match: 'extra-package', bundler: 'webpack' },
      ]);
      expect(config.features).toEqual({
        disableSourceMaps: true,
        enableCloudHosted: false,
      });
      expect(config.define).toEqual({
        APP_NAME: '"MyApp"',
        DEBUG: 'true',
      });
    });

    it('expands routes with custom config filename', async () => {
      appPath = await createTestFileStructure({
        'custom-routes.json': {
          routes: [
            { match: '/', exportEntry: '.' },
            { match: '/test', entry: './src/test.ts' },
          ],
        } satisfies AppConfig,
        'package.json': { name: 'test', version: '1.0.0', module: './lib/index.js' },
        'src/index.ts': '',
        'src/test.ts': '',
        'lib/index.js': '',
      });

      const config = await readConfig(appPath, { configFileName: 'custom-routes.json' });

      expect(config.routes).toEqual([
        {
          match: '/',
          entry: [{ importPath: '.', sourcePath: './src/index.ts' }],
        },
        {
          match: '/test',
          entry: [{ importPath: './lib/test.js', sourcePath: './src/test.ts' }],
        },
      ]);
    });

    it('throws error when custom file does not exist', async () => {
      appPath = await createTestFileStructure({
        'cloudpack.config.json': { helpMessage: 'default' } satisfies AppConfig,
      });

      await expect(() => readConfig(appPath, { configFileName: 'non-existent.json' })).rejects.toThrow(
        'Config file not found: non-existent.json',
      );
    });
  });
});
